import { TutorialService } from 'src/app/shared/services/communication_services/tutorial.service';
import { AnalyticsService } from 'src/app/shared/services/analytics/analytics.service';
import { PromptsInChatType } from 'src/app/shared/services/prompts-in-chat.service';
import { NextMessageAnswer } from 'src/app/shared/types/next-message.interface';
import { PaywallService } from 'src/app/shared/services/paywall.service';
import { ChatAnswersService } from '../services/chat-answers.service';
import { ChatStateService } from '../services/chat-state.service';
import { ChatTrialService } from '../services/chat-trial.service';
import { ChatService } from '../services/chat.service';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subscription, map } from 'rxjs';
import { SettingsDataService } from 'src/app/shared/services/communication_services/settingsData.service';
import { UserPreference } from 'src/app/shared/enums/user-preference.enum';
import { environment } from 'src/environments/environment';
import { BuildVersion } from 'src/environments/environment-model.interface';

enum CustomMessageId {
  agePrompt = 'AGE_PROMPT',
  agreementPrompt = 'AGREEMENT_PROMPT',
}

type AskPhotoTag = {
  tag: string;
  text: string;
  img: string;
  message: string;
  hideForLegal?: boolean;
};

@Component({
  selector: 'app-answer-input',
  templateUrl: './answer-input.component.html',
  styleUrls: ['./answer-input.component.scss'],
})
export class ChatAnswerInputComponent implements OnInit, OnDestroy {
  @ViewChild('aiInputField')
  private _aiInputField!: ElementRef<HTMLInputElement>;

  public inputField = '';

  public readonly answers$ = this._chatAnswersService.answerMessage$.pipe(
    map((m) => m?.answers ?? []),
  );
  public readonly tutorialStep$ = this._tutorialService.currentStep$;
  public readonly chatState$ = this._chatStateService.chatState$;
  public readonly isAiChat$ = this._chatStateService.isAiChat$;
  public readonly isTutorialChat$ = this._tutorialService.isActive$;
  public readonly isCustomChat$ = this._chatStateService.isCustomChat$;
  public readonly aiAnswersSuggestions$ =
    this._chatStateService.aiAnswersSuggestions$;

  public readonly isSuggestionsVisible$ = this.answers$.pipe(
    map((answers) => {
      if (answers.length === 0) {
        return false;
      }
      return (
        answers.some((answer) => answer.type === 'input') &&
        this.aiAnswersSuggestions$.value !== null &&
        this.aiAnswersSuggestions$.value.length > 0
      );
    }),
  );

  public readonly maxMessageLength = this._chatAnswersService.maxMessageLength;
  public isPhotoRequest = false;
  public isTagMenuOpen = false;
  public isBoy = false;
  public askTags: AskPhotoTag[] = [
    {
      tag: 'selfie',
      text: 'Selfie',
      img: '/assets/img/emoji/wink.png',
      message: 'Show me Selfie',
    },
    {
      tag: 'bikini',
      text: 'Hot bikini',
      img: '/assets/img/emoji/bikini.png',
      message: 'Show me Hot bikini',
    },
    {
      tag: 'lingerie',
      text: 'Sexy lingerie',
      img: '/assets/img/emoji/biting_lip.png',
      message: 'Show me Sexy lingerie',
      hideForLegal: true,
    },
    {
      tag: 'nudes',
      text: 'Spicy photo',
      img: '/assets/img/emoji/underage.png',
      message: 'Show me Spicy photo',
      hideForLegal: true,
    },
    {
      tag: 'custom',
      text: 'Custom',
      img: '/assets/img/emoji/fire.png',
      message: 'Show me ',
    },
  ];

  private _subscriptions: Subscription[] = [];

  constructor(
    private _settingsDataService: SettingsDataService,
    private _chatAnswersService: ChatAnswersService,
    private _analyticsService: AnalyticsService,
    private _chatStateService: ChatStateService,
    private _chatTrialService: ChatTrialService,
    private _tutorialService: TutorialService,
    private _paywallService: PaywallService,
    private _chatService: ChatService,
    private _platform: Platform,
  ) {
    this._subscriptions.push(
      this._chatAnswersService.answerMessage$.subscribe((res) => {
        this._tutorialService.isAnswersVisible$.next(!!res);
      }),
    );

    if (environment.buildVersion === BuildVersion.legal) {
      this.askTags = this.askTags.filter((tag) => !tag.hideForLegal);
    }
  }

  public ngOnInit(): void {
    this.isBoy =
      this._settingsDataService.updateSettingsData$.value.preference ===
      UserPreference.boy;
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach((s) => s.unsubscribe());
  }

  public sendAnswer(answer: NextMessageAnswer) {
    if (answer.isNicknameInput) {
      this.submitNickname(answer.localizedMessageText.en, false);
      return;
    }

    const customMessageId = this._chatAnswersService.answerMessage$.value
      .id as CustomMessageId;

    if (Object.values(CustomMessageId).includes(customMessageId)) {
      this.handleCustomQuestionAnswering(answer, customMessageId);
      return;
    }

    this._subscriptions.push(
      this._chatAnswersService.clickOnAnswer(answer).subscribe(),
    );
  }

  public clickEmpty() {
    if (this._chatStateService.chatState$.value === 'aiOffline') {
      this._chatService
        .getNextMessages(this._chatStateService.chatId)
        .subscribe();
    } else if (this._chatStateService.chatState$.value === 'trialBlocked') {
      this._chatTrialService.openTrialPurchase();
    }
  }

  public openPaywall() {
    this._paywallService.showPaywall('chat').subscribe();
  }

  public submitInput(answer: NextMessageAnswer, event?: Event) {
    event?.preventDefault();
    if (!this.inputField.trim().length) {
      return;
    }

    if (this.isPhotoRequest && !this.checkAskAiPossibility(false)) {
      return;
    }

    if (this.isPhotoRequest && this.inputField.trim() === 'Show me') return;

    const input = this.inputField;
    this.inputField = '';

    if (answer.isNicknameInput) {
      this.submitNickname(input, true);
    } else {
      const command = this.isPhotoRequest
        ? this._chatAnswersService.submitAskTag(input, null)
        : this._chatAnswersService.submitInput(input);

      this._subscriptions.push(
        command.subscribe((result) => {
          if (!result) {
            this.inputField = input;
          } else {
            this.cancelPhotoRequest();
          }
        }),
      );
    }
  }

  public aiSuggestionClick(suggestion: string) {
    this.inputField = '';
    this._subscriptions.push(
      this._chatAnswersService.submitInput(suggestion).subscribe(),
    );
  }

  private checkAskAiPossibility(isTag: boolean): boolean {
    const attemptsRemain = isTag
      ? this._settingsDataService.updateSettingsData$.value.freeAiAskTagsCount
      : this._settingsDataService.updateSettingsData$.value.freeAiAskCount;

    if (attemptsRemain === 0 && !this._settingsDataService.isPremiumActive) {
      this.openPaywall();
      return false;
    }

    return true;
  }

  public onInputChange(text: string) {
    if (this.isPhotoRequest && !text.startsWith('Show me ')) {
      this.cancelPhotoRequest();
    }
  }

  public toggleTagMenu(): void {
    this.isTagMenuOpen = !this.isTagMenuOpen;
  }

  public closeTagMenu(): void {
    if (this.isTagMenuOpen) {
      this.isTagMenuOpen = false;
    }
  }

  public tagClick(tag: string): void {
    if (!this.checkAskAiPossibility(tag !== 'custom')) {
      return;
    }

    if (tag === 'custom') {
      this.showPhotoRequest();
    } else {
      const el = this.askTags.find((el) => el.tag === tag);

      this._chatAnswersService.submitAskTag(el.message, el.tag).subscribe();
    }

    this.closeTagMenu();
  }

  public showPhotoRequest(): void {
    this.isPhotoRequest = true;
    this.inputField = 'Show me ';
    this._aiInputField.nativeElement.focus();
  }

  public cancelPhotoRequest(): void {
    if (!this.isPhotoRequest) return;
    this.inputField = '';
    this.isPhotoRequest = false;
  }

  private submitNickname(nickname: string, handWrited: boolean) {
    this._subscriptions.push(
      this._chatAnswersService.submitNickname(nickname, handWrited).subscribe(),
    );
  }

  private handleCustomQuestionAnswering(
    answer: NextMessageAnswer,
    customMessageId: CustomMessageId,
  ) {
    switch (customMessageId) {
      case CustomMessageId.agePrompt:
        if (answer.id < 0) {
          this._analyticsService.ageIsSet(false);
          const storeLink =
            'https://apps.apple.com/cy/app/lovechats/id6444935703';
          // const storeLink = this._platform.is('ios')
          //   ? 'https://apps.apple.com/cy/app/lovechats/id6444935703'
          //   : 'https://play.google.com/store/apps/details?id=app.perchapps.lovechats';
          this._chatAnswersService.answerMessage$.next(null);
          setTimeout(() => (location.href = storeLink), 1000); //  Settimout to set analytics before redirect
        } else {
          this._subscriptions.push(
            this._chatAnswersService
              .customAnswer(PromptsInChatType.ageOver18, answer)
              .subscribe(),
          );
        }

        break;
      case CustomMessageId.agreementPrompt:
        this._subscriptions.push(
          this._chatAnswersService
            .customAnswer(PromptsInChatType.agreement, answer)
            .subscribe(),
        );
        break;
    }
  }
}
